import { useEffect, useMemo, useState } from 'react';
import { Users, Star, Calendar, TrendingUp, Search } from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, StatCard, Badge, EmptyState, Input,
} from '../components/ui';

type Booking = {
  id: string;
  user_id: string;
  scheduled_at: string;
  status: string;
  total_amount: number;
  currency: string;
  lesson_type: string;
  environment: string;
  profiles: { full_name: string | null; phone: string | null } | null;
};

type Student = {
  user_id: string;
  full_name: string | null;
  phone: string | null;
  lessons: number;
  completed: number;
  upcoming: number;
  totalSpend: number;
  currency: string;
  lastLesson: string | null;
  nextLesson: string | null;
};

export default function CoachStudents({ coachId }: { coachId: string }) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('coach_bookings')
        .select(`
          id, user_id, scheduled_at, status, total_amount, currency,
          lesson_type, environment,
          profiles!user_id(full_name, phone)
        `)
        .eq('coach_id', coachId)
        .order('scheduled_at', { ascending: false })
        .limit(1000);
      setBookings((data ?? []) as unknown as Booking[]);
      setLoading(false);
    })();
  }, [coachId]);

  // Roll bookings up into per-student summaries.
  const students: Student[] = useMemo(() => {
    const now = Date.now();
    const byUser = new Map<string, Student>();
    for (const b of bookings) {
      if (!b.user_id) continue;
      if (!['confirmed', 'completed'].includes(b.status)) continue;
      const cur = byUser.get(b.user_id) ?? {
        user_id: b.user_id,
        full_name: b.profiles?.full_name ?? null,
        phone: b.profiles?.phone ?? null,
        lessons: 0, completed: 0, upcoming: 0,
        totalSpend: 0, currency: b.currency,
        lastLesson: null, nextLesson: null,
      };
      cur.lessons += 1;
      const when = new Date(b.scheduled_at).getTime();
      if (when < now || b.status === 'completed') {
        cur.completed += 1;
        if (!cur.lastLesson || when > new Date(cur.lastLesson).getTime()) {
          cur.lastLesson = b.scheduled_at;
        }
      } else {
        cur.upcoming += 1;
        if (!cur.nextLesson || when < new Date(cur.nextLesson).getTime()) {
          cur.nextLesson = b.scheduled_at;
        }
      }
      cur.totalSpend += Number(b.total_amount ?? 0);
      byUser.set(b.user_id, cur);
    }
    return Array.from(byUser.values()).sort((a, b) => b.lessons - a.lessons);
  }, [bookings]);

  const filtered = useMemo(() => {
    if (!q.trim()) return students;
    const s = q.trim().toLowerCase();
    return students.filter((st) =>
      st.full_name?.toLowerCase().includes(s)
      || st.phone?.toLowerCase().includes(s),
    );
  }, [students, q]);

  const totalLessons = students.reduce((s, x) => s + x.lessons, 0);
  const totalSpend   = students.reduce((s, x) => s + x.totalSpend, 0);
  const currency     = students[0]?.currency ?? 'AED';

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Coach"
        title="My students"
        subtitle="Players who've taken a lesson with you, sorted by lesson count."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard icon={Users}      label="Unique students"   value={students.length} />
        <StatCard icon={Calendar}   label="Lessons taught"    value={totalLessons} />
        <StatCard icon={TrendingUp} label="Lifetime revenue"
          value={totalLessons > 0 ? `${currency} ${Math.round(totalSpend).toLocaleString()}` : '—'} />
      </div>

      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or phone" className="pl-9" />
        </div>
      </div>

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading students…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Star}
            title={students.length === 0 ? "No students yet" : "No students match that search"}
            description={students.length === 0
              ? "Once someone books a lesson with you, they'll show up here."
              : "Try a different name or phone fragment."}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Student</th>
                <th className="text-left px-5 py-3 font-semibold">Lessons</th>
                <th className="text-left px-5 py-3 font-semibold">Last / Next</th>
                <th className="text-right px-5 py-3 font-semibold">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((st) => (
                <tr key={st.user_id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3">
                    <div className="font-medium text-gray-900">{st.full_name ?? 'AceRent player'}</div>
                    {st.phone && (
                      <div className="text-xs text-gray-500">{st.phone}</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-gray-900">{st.lessons} total</div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                      <Badge tone="gray">{st.completed} done</Badge>
                      {st.upcoming > 0 && <Badge tone="green">{st.upcoming} upcoming</Badge>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {st.lastLesson && (
                      <div className="text-gray-700">
                        Last: {fmt(st.lastLesson)}
                      </div>
                    )}
                    {st.nextLesson && (
                      <div className="text-[#2d5016] font-medium">
                        Next: {fmt(st.nextLesson)}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right font-medium">
                    {st.currency} {Math.round(st.totalSpend).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
